/**
 * SOLID Review
 * S — Strong. One concern: the local player's moves at the table — each built from the snapshot as a `ClientTable`,
 *     asked, and answered through a `ClientDecision`. The rules are the aggregate's.
 * O — Good. A new move is a plan and an answer here, each a row in the table, and their rules on the aggregate.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the moves' table and nothing else.
 * D — Strong. The table aggregate, the decision builder, the server's dice ranking and the schemas.
 */
import { ClientTable } from '../../table/ClientTable';
import { DiceRanking } from '../../dice/DiceRanking';
import { ClientDecision, type IConfigClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientTableInput } from '../../ports/schemas/ClientTable';
import type { IPropsClientSessionChanges, IPropsClientPersist } from '../../ports/schemas/ClientSession';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type {
  ICommandAnnounce, ICommandLeave, ICommandGameStarted, ICommandRoundMoveAnswered, ICommandPeeked, ICommandClaimAccepted,
  ICommandBelieveAccepted, ICommandPlayAgainAnswered, IPayloadStartGame, IPayloadRoundMove, IPayloadAnnounce, IPayloadBelieve,
  IPayloadPlayAgain, IEntityLeave,
} from '../../ports/schemas/TableMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TTablePlayOp =
  | 'planStartGame' | 'gameStarted' | 'planRoll' | 'rolled' | 'planPeek' | 'peeked' | 'planAnnounce' | 'claimAccepted'
  | 'planBelieve' | 'believeAccepted' | 'planRollFirst' | 'rollFirstAccepted' | 'planLift' | 'liftAnswered' | 'planPlayAgain'
  | 'playAgainAnswered' | 'leaveTable';

/** The table moved: the store stamps the activity the media's idle rule reads. */
const MOVED: IConfigClientDecision = { markActivity: true };
const STILL: IConfigClientDecision = {};
const RESET: IConfigClientDecision = { reset: true };

/**
 * THE LOCAL PLAYER'S MOVES (frontend-domain-oo P5, 2026-10-26; handbook §28.272) — `decisions/table/localMoves.ts`'s
 * seventeen decide functions as a service: LobbyPanel's Start, GameSceneCore's cup tap and ControlPanel's buttons
 * (FE1). Each pair is the click handler it replaced, split at its await: the PLAN says what to send, or refuses; the
 * ANSWER applies what came back. An answer is a move, which stamps the activity — but the game's start, as its
 * handler did not.
 */
export class TablePlay implements IServiceDecisionTable<TTablePlayOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TTablePlayOp> = {
    planStartGame: (input) => this.planStartGame(input),
    gameStarted: (input, command) => this.gameStarted(input, command),
    planRoll: (input) => this.planRoll(input),
    rolled: (input, command) => this.rolled(input, command),
    planPeek: (input) => this.planPeek(input),
    peeked: (input, command) => this.peeked(input, command),
    planAnnounce: (input, command) => this.planAnnounce(input, command),
    claimAccepted: (input, command) => this.claimAccepted(input, command),
    planBelieve: (input) => this.planBelieve(input),
    believeAccepted: (input, command) => this.believeAccepted(input, command),
    planRollFirst: (input) => this.planRollFirst(input),
    rollFirstAccepted: (input, command) => this.rollFirstAccepted(input, command),
    planLift: (input) => this.planLift(input),
    liftAnswered: (input, command) => this.liftAnswered(input, command),
    planPlayAgain: (input) => this.planPlayAgain(input),
    playAgainAnswered: (input, command) => this.playAgainAnswered(input, command),
    leaveTable: (input, command) => this.leaveTable(input, command),
  };

  planStartGame(input: IPropsClientTableInput): TClientDecision<IPayloadStartGame> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planStartGame());
  }

  gameStarted(input: IPropsClientTableInput, command: ICommandGameStarted): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, STILL).acked(table.gameStarted(command));
  }

  planRoll(input: IPropsClientTableInput): TClientDecision<IPayloadRoundMove> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planRoll());
  }

  rolled(input: IPropsClientTableInput, command: ICommandRoundMoveAnswered): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.rolled(command));
  }

  planPeek(input: IPropsClientTableInput): TClientDecision<IPayloadRoundMove> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planPeek());
  }

  peeked(input: IPropsClientTableInput, command: ICommandPeeked): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.peeked(command));
  }

  planAnnounce(input: IPropsClientTableInput, command: ICommandAnnounce): TClientDecision<IPayloadAnnounce> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planAnnounce(command.score));
  }

  claimAccepted(input: IPropsClientTableInput, command: ICommandClaimAccepted): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.claimAccepted(command));
  }

  planBelieve(input: IPropsClientTableInput): TClientDecision<IPayloadBelieve> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planBelieve(DiceRanking.of(input.diceRanking)));
  }

  believeAccepted(input: IPropsClientTableInput, command: ICommandBelieveAccepted): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.believeAccepted(command));
  }

  planRollFirst(input: IPropsClientTableInput): TClientDecision<IPayloadBelieve> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planRollFirst());
  }

  rollFirstAccepted(input: IPropsClientTableInput, command: ICommandBelieveAccepted): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.rollFirstAccepted(command));
  }

  /** The lift's plan moves the table before the call: the server's decider is cleared. */
  planLift(input: IPropsClientTableInput): TClientDecision<IPayloadRoundMove> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planLift());
  }

  liftAnswered(input: IPropsClientTableInput, command: ICommandRoundMoveAnswered): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.liftAnswered(command));
  }

  /** Play Again's plan moves the table before the call: every seat back to full lives. */
  planPlayAgain(input: IPropsClientTableInput): TClientDecision<IPayloadPlayAgain> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table).judging(table.planPlayAgain());
  }

  playAgainAnswered(input: IPropsClientTableInput, command: ICommandPlayAgainAnswered): TClientDecision<IEntityTableAck> {
    const table = ClientTable.fromSnapshot(input.table);
    return ClientDecision.ofTable(input, table, MOVED).acked(table.playAgainAnswered(command));
  }

  /**
   * Leave the table: it resets (`ClientTable.leave`). FE2: and what every caller did after the reset — ControlPanel's
   * back-to-menu, the room-ended overlay and the leave-waiting overlay each forgot the stored game and went back to the
   * welcome screen, and the overlays dropped "restoring". One decision, so no caller can do half of it.
   */
  leaveTable(input: IPropsClientTableInput, command: ICommandLeave): TClientDecision<IEntityLeave> {
    const table = ClientTable.fromSnapshot(input.table);
    const leave: IEntityLeave = { leave: table.leave(command.notifyServer) };
    const welcome: IPropsClientSessionChanges = { screen: 'WELCOME', restoring: false };
    const forgetGame: IPropsClientPersist = { resume: null };
    return ClientDecision.ofTable(input, table, RESET).alongside(welcome).persisting(forgetGame).answering(leave);
  }
}
